import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import EmptyScreen from '../../../../../../components/empty-screen/EmptyScreen';
import PcfTable from '../../../../../../components/pcf-table/PcfTable';
import { useGetFootprintsByProductId } from '../../../../../../hooks/api/footprints';
import { useAuth } from '../../../../../../hooks/auth/useAuth';
import {
  CREATION_METHOD,
  FootPrintStatus,
  IFootPrintTableRow,
} from '../../../../../../shared/interfaces/IFootprint';
import FootprintDetailsPage from '../../../../../footprint-details/FootprintDetailsPage';
import AddPcfButton from './components/add-pcf-button/AddPcfButton';
import CalculatePcfButton from './components/calculate-pcf-button/CalculatePcfButton';
import CopyAsNewButton from './components/copy-as-new-button/CopyAsNewButton';
import DiscardPcfButton from './components/discard-pcf-button/DiscardPcfButton';
import InfoMessage from './components/info-message/InfoMessage';
import CalculationsOverview from './components/pcf-wizard/components/calculations-overview/CalculationsOverview';
import PcfWizard from './components/pcf-wizard/PcfWizard';

interface PcfTabProps {
  isReadOnly?: boolean;
}

const PcfTab = ({ isReadOnly }: PcfTabProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'pcfTable' | 'calculate'>('pcfTable');
  const { t } = useTranslation();
  const { user } = useAuth();

  // Read params from URL
  const { productId, footprintId, calculationId } = useParams() as {
    productId: string;
    footprintId: string;
    calculationId: string;
  };

  const { data: footprints, isLoading } = useGetFootprintsByProductId(productId);

  // Listen for location changes to set view mode
  // If user navigates to /pcf, always set viewMode to 'pcfTable'
  useEffect(() => {
    if (location.pathname.match(`/products/${productId}/pcf`)) {
      setViewMode('pcfTable');
    }
  }, [location]);

  // Determine if the info message should be visible for basic users
  const isInfoMessageVisible = useMemo(() => {
    const publishedFootprint = footprints?.some((footprint) => {
      return footprint.status === FootPrintStatus.PUBLISHED;
    });

    return publishedFootprint && user?.company?.license === 'basic';
  }, [footprints]);

  const canAddPcf = useMemo(() => {
    // Checking for any draft pcf, because Create Pcf is only possible when there is no draft pcf.
    const isDraftExist = footprints?.some((footprint) => {
      return footprint.status === FootPrintStatus.DRAFT;
    });

    return !isDraftExist && !isReadOnly;
  }, [footprints, isReadOnly]);

  // based on pathname return calculations overview or pcf wizard
  if (location.pathname.includes('calculations')) {
    if (location.pathname.endsWith('calculations')) {
      return <CalculationsOverview />;
    }
    if (calculationId) return <PcfWizard setViewMode={setViewMode} />;
  }

  if (footprintId) {
    if (location.pathname.endsWith('copy')) {
      return <PcfWizard setViewMode={setViewMode} />;
    }

    return <FootprintDetailsPage />;
  }

  const draftPCFExists = footprints?.some((footprint) => {
    return footprint.status === FootPrintStatus.DRAFT;
  });

  const actionTemplate = (rowData: IFootPrintTableRow): JSX.Element | null => {
    if (rowData.status === FootPrintStatus.PUBLISHED && !draftPCFExists) {
      return (
        <CopyAsNewButton
          footprintId={rowData.id}
          productId={Number(productId)}
          disabled={isReadOnly}
        />
      );
    }

    if (rowData.status !== FootPrintStatus.DRAFT) {
      return <></>;
    }

    return (
      <DiscardPcfButton
        footprintId={rowData.id}
        productId={Number(productId)}
        disabled={isReadOnly}
      />
    );
  };

  const emptyScreenContent = (
    <EmptyScreen>
      <div className='flex flex-column md:flex-row gap-2 justify-content-center w-full'>
        {/* Calculate PCF */}
        <div className='flex flex-column align-items-center w-full md:w-4'>
          <CalculatePcfButton
            disabled={isReadOnly}
            onClick={() => {
              setViewMode('calculate');
            }}
          />
          <h5 className='text-center'>
            {t('productDetailsPage.tabs.pcfTab.emptyState.calculatePcf.title')}
          </h5>
          <p className='text-center'>
            {t('productDetailsPage.tabs.pcfTab.emptyState.calculatePcf.description')}
          </p>
        </div>

        <Divider layout='vertical' className='hidden md:block' />
        <Divider layout='horizontal' className='block md:hidden' />

        {/* Add PCF */}
        <div className='flex flex-column align-items-center w-full md:w-4'>
          <AddPcfButton disabled={isReadOnly} />

          <h5 className='text-center'>
            {t('productDetailsPage.tabs.pcfTab.emptyState.addPcf.title')}
          </h5>
          <p className='text-center'>
            {t('productDetailsPage.tabs.pcfTab.emptyState.addPcf.description')}
          </p>
        </div>
      </div>
    </EmptyScreen>
  );

  const pcfTableContent = (
    <div>
      <h1 className='text-xl md:text-2xl font-medium p-0 my-3'>
        {t('productDetailsPage.tabs.pcfTab.pcfHeader')}
      </h1>
      <div className='flex flex-column gap-4'>
        {isInfoMessageVisible && <InfoMessage />}

        <PcfTable
          footprints={footprints || []}
          isLoading={isLoading}
          onRowClick={(rowData: IFootPrintTableRow) => {
            if (
              rowData?.creationMethod === CREATION_METHOD.CALCULATION &&
              rowData?.status === FootPrintStatus.DRAFT
            ) {
              const href = `/products/${productId}/pcf/footprint/${rowData.id}/calculations`;
              navigate(href);
            } else {
              const href = `/products/${productId}/pcf/footprint/${rowData.id}`;
              navigate(href);
            }
          }}
          actions={actionTemplate}
        />
        <div className='mt-3 flex flex-row gap-4'>
          <AddPcfButton disabled={!canAddPcf} />

          <CalculatePcfButton
            disabled={!canAddPcf}
            onClick={() => {
              setViewMode && setViewMode('calculate');
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderLoadingContent = (): JSX.Element => {
    return (
      <div>
        <Skeleton
          height='15rem'
          className='flex w-full flex-row justify-content-center mb-3 border-round-xl'
        />
        <Skeleton height='2.5rem' width='10rem' className='border-round-lg' />
      </div>
    );
  };

  // Main content entry-point
  // Determine the content based on viewMode
  const renderPcfTabContent = (): JSX.Element => {
    if (viewMode === 'pcfTable') {
      // If there are footprints, show the table content
      if (footprints?.length) {
        return pcfTableContent;
      }

      // Otherwise, show the empty screen content
      return emptyScreenContent;
    } else if (viewMode === 'calculate') {
      return <PcfWizard setViewMode={setViewMode} />;
    }

    return <></>;
  };

  return isLoading ? renderLoadingContent() : renderPcfTabContent();
};

export default PcfTab;
